import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NotesListTab from './notes-list-tab';
import {
  createProspectNotesListRequest,
  deleteProspectNoteRequest,
  getProspectNotesRequest,
  prospectNotePinRequest,
  updateProspectNoteRequest,
  updateProspectNoteVisibilityRequest,
} from '../../../../../../extra-actions';
import { NotesListProps } from './utils/types';
import {
  resetDeleteAttachmentRequest,
  resetUploadAttachmentRequest,
} from '../../../../../../../sequence/sequence-slice';
import {
  deleteAttachmentRequest,
  uploadAttachmentRequest,
} from '../../../../../../../sequence/extra-actions';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
  DELETE_ATTACHMENT_CLIENT_SOURCE_PROSPECT_NOTES,
} from '../../../../../../../sequence/types';
import { RootState } from '../../../../../../../../store/root-reducer';
import {
  CreateProspectNoteProp,
  getProspectNotesParams,
  ProspectNotePin,
  UpdateProspectNoteProps,
  UpdateProspectNoteVisibilityProps,
} from '../../../../../../types';
import {
  resetDeleteProspectNoteRequest,
  resetCreateProspectNotesListRequest,
  resetProspectNotePinRequest,
  resetGetProspectNotesRequest,
  resetUpdateProspectNoteRequest,
  resetUpdateProspectNoteVisibilityRequest as resetUpdateProspectNoteVisibilityRequestAction,
  setProspectActivityCount,
} from '../../../../../../prospect-slice';

const NotesListTabContainer = memo((props: NotesListProps) => {
  const dispatch = useDispatch();

  const handleSendAttachmentUploadRequest = useCallback(
    (payload: AttachmentUploadPayload) => {
      dispatch(uploadAttachmentRequest(payload));
    },
    [dispatch],
  );

  const handleSendDeleteAttachmentRequest = useCallback(
    (payload: AttachmentDeletePayload) => {
      dispatch(
        deleteAttachmentRequest({
          ...payload,
          clientSource: DELETE_ATTACHMENT_CLIENT_SOURCE_PROSPECT_NOTES,
        }),
      );
    },
    [dispatch],
  );

  const handleResetUploadAttachmentRequest = useCallback(() => {
    dispatch(resetUploadAttachmentRequest());
  }, [dispatch]);

  const handleResetDeleteAttachmentRequest = useCallback(() => {
    dispatch(resetDeleteAttachmentRequest());
  }, [dispatch]);

  const fetchNotesProspectList = (payload: getProspectNotesParams) => {
    dispatch(getProspectNotesRequest(payload));
  };

  const createProspectNote = (payload: CreateProspectNoteProp) => {
    dispatch(createProspectNotesListRequest(payload));
  };

  const resetCreateProspectNotesListRequests = () => {
    dispatch(resetCreateProspectNotesListRequest());
  };

  const deleteProspectNotesRequest = (noteId: number) => {
    dispatch(deleteProspectNoteRequest(noteId));
  };

  const prospectNotePinRequests = (
    noteId: number,
    payload: ProspectNotePin,
  ) => {
    dispatch(prospectNotePinRequest({ noteId, payload }));
  };
  const resetDeleteProspectNoteRequests = () => {
    dispatch(resetDeleteProspectNoteRequest());
  };
  const resetProspectNotePinRequests = () => {
    dispatch(resetProspectNotePinRequest());
  };

  const updateProspectNotesRequest = (payload: UpdateProspectNoteProps) => {
    dispatch(updateProspectNoteRequest(payload));
  };

  const resetGetProspectNotesRequests = () => {
    dispatch(resetGetProspectNotesRequest());
  };
  const resetUpdateProspectNote = () => {
    dispatch(resetUpdateProspectNoteRequest());
  };

  const resetUpdateProspectNoteVisibilityRequest = () => {
    dispatch(resetUpdateProspectNoteVisibilityRequestAction());
  };

  const updateProspectNotesVisibilityRequest = (
    noteId: number,
    payload: UpdateProspectNoteVisibilityProps,
  ) => {
    dispatch(updateProspectNoteVisibilityRequest({ noteId, payload }));
  };

  const editProspectAcitivityCount = (payload: any) => {
    dispatch(setProspectActivityCount(payload));
  };

  const {
    uploadAttachmentRequestStatus,
    uploadAttachmentRequestError,
    uploadedAttachment,
    deleteAttachmentRequestStatus,
    deleteAttachmentRequestError,
    deletedAttachmentId,
    deleteAttachmentClientSource,
  } = useSelector((state: RootState) => ({
    uploadAttachmentRequestStatus:
      state.sequence.uploadAttachmentRequest.status,
    uploadAttachmentRequestError: state.sequence.uploadAttachmentRequest.error,
    uploadedAttachment: state.sequence.uploadedAttachment,
    deleteAttachmentRequestStatus:
      state.sequence.deleteAttachmentRequest.status,
    deleteAttachmentRequestError: state.sequence.deleteAttachmentRequest.error,
    deletedAttachmentId: state.sequence.deletedAttachmentId,
    deleteAttachmentClientSource: state.sequence.deleteAttachmentClientSource,
  }));

  const createProspectNoteRequestStatus = useSelector(
    (state: RootState) => state.prospect.createProspectNotesListRequest.status,
  );
  const createProspectNoteRequestMessage = useSelector(
    (state: RootState) => state.prospect.createProspectNotesListRequest.message,
  );
  const createProspectNoteResponse = useSelector(
    (state: RootState) => state.prospect.createProspectNoteResponse,
  );
  const deleteProspectNotesRequestsStatus = useSelector(
    (state: RootState) => state.prospect.deleteProspectNoteRequest,
  );
  const prospectNotesListingResponse = useSelector(
    (state: RootState) => state.prospect.prospectNotesList,
  );
  const getProspectNotesRequestStatus = useSelector(
    (state: RootState) => state.prospect.getProspectNotesRequest.status,
  );

  const getProspectNotesPinRequestStatus = useSelector(
    (state: RootState) => state.prospect.prospectNotePinRequest.status,
  );
  const getProspectNotesPinRequestMessage = useSelector(
    (state: RootState) => state.prospect.prospectNotePinRequest.message,
  );

  const getUpdateProspectNoteVisibiityRequestStatus = useSelector(
    (state: RootState) =>
      state.prospect.updateProspectNoteVisibilityRequest.status,
  );
  const updateProspectNoteVisibilityResponse = useSelector(
    (state: RootState) => state.prospect.updateProspectNoteVisibilityResponse,
  );
  const uploadedAttachmentRes = useSelector(
    (state: RootState) => state?.sequence?.uploadedAttachment,
  );

  const updateProspectNoteRequestState = useSelector(
    (state: RootState) => state?.prospect?.updateProspectNoteRequest,
  );
  const updateProspectNoteResponse = useSelector(
    (state: RootState) => state.prospect.updateProspectNoteResponse,
  );

  const loginUserId = useSelector((state: RootState) => state?.home?.userId);
  const planCode = useSelector(
    (state: RootState) => state?.home?.subscription?.planCode,
  );
  const userTimezone = useSelector((state: RootState) => state?.home?.timeZone);

  const prospectActivityCount = useSelector(
    (state: RootState) => state.prospect.prospectActivityCount,
  );

  return (
    <NotesListTab
      {...props}
      handleSendAttachmentUploadRequest={handleSendAttachmentUploadRequest}
      handleSendDeleteAttachmentRequest={handleSendDeleteAttachmentRequest}
      handleResetUploadAttachmentRequest={handleResetUploadAttachmentRequest}
      handleResetDeleteAttachmentRequest={handleResetDeleteAttachmentRequest}
      fetchNotesProspectList={fetchNotesProspectList}
      createProspectNote={createProspectNote}
      resetCreateProspectNotesListRequests={
        resetCreateProspectNotesListRequests
      }
      resetGetProspectNotesRequests={resetGetProspectNotesRequests}
      deleteProspectNotesRequest={deleteProspectNotesRequest}
      prospectNotePinRequests={prospectNotePinRequests}
      resetDeleteProspectNoteRequests={resetDeleteProspectNoteRequests}
      resetProspectNotePinRequests={resetProspectNotePinRequests}
      updateProspectNotesRequest={updateProspectNotesRequest}
      resetUpdateProspectNote={resetUpdateProspectNote}
      uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
      uploadAttachmentRequestError={uploadAttachmentRequestError}
      uploadedAttachment={uploadedAttachment}
      deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
      deleteAttachmentRequestError={deleteAttachmentRequestError}
      deletedAttachmentId={deletedAttachmentId}
      deleteAttachmentClientSource={deleteAttachmentClientSource}
      createProspectNoteRequestStatus={createProspectNoteRequestStatus}
      createProspectNoteRequestMessage={createProspectNoteRequestMessage}
      createProspectNoteResponse={createProspectNoteResponse}
      deleteProspectNotesRequestsStatus={deleteProspectNotesRequestsStatus}
      prospectNotesListingResponse={prospectNotesListingResponse}
      getProspectNotesRequestStatus={getProspectNotesRequestStatus}
      getProspectNotesPinRequestStatus={getProspectNotesPinRequestStatus}
      getProspectNotesPinRequestMessage={getProspectNotesPinRequestMessage}
      updateProspectNotesVisibilityRequest={
        updateProspectNotesVisibilityRequest
      }
      resetUpdateProspectNoteVisibilityRequest={
        resetUpdateProspectNoteVisibilityRequest
      }
      getUpdateProspectNoteVisibiityRequestStatus={
        getUpdateProspectNoteVisibiityRequestStatus
      }
      updateProspectNoteVisibilityResponse={
        updateProspectNoteVisibilityResponse
      }
      uploadedAttachmentRes={uploadedAttachmentRes}
      updateProspectNoteRequestState={updateProspectNoteRequestState}
      updateProspectNoteResponse={updateProspectNoteResponse}
      loginUserId={loginUserId}
      planCode={planCode}
      userTimezone={userTimezone}
      prospectActivityCount={prospectActivityCount}
      editProspectAcitivityCount={editProspectAcitivityCount}
    />
  );
});

export default NotesListTabContainer;
